/*!
 * RuYofication — shared, dependency-free «ё»-restoration for Russian text.
 *
 * A tiny tool-agnostic wrapper over the self-hosted eyo-kernel SAFE
 * dictionary (public/vendor/eyo/bundle.safe.js — unambiguous е→ё
 * replacements only, ~60k word forms). Any tool can adopt it:
 *
 *   RuYofication.ready().then(function (ok) {
 *       if (!ok) return;                       // dictionary failed to load
 *       var r = RuYofication.restore(text);    // { text, count }
 *       if (r.count > 0) el.value = r.text;
 *   });
 *
 * API:
 *   ready(): Promise<boolean> — lazy-loads the dictionary script ONCE
 *       (same-origin), resolves true when window.safeEyo is usable.
 *       A failed load resolves false and allows a retry on the next call.
 *   restore(text): { text: string, count: number } — applies the SAFE
 *       dictionary; count = number of replaced word occurrences. Never
 *       throws: on any failure returns the input unchanged with count 0.
 *   restoreWords(words): number — the same on each {text} of a word list,
 *       in place (timed transcripts); returns how many words changed.
 *
 * ES2019-safe on purpose (no optional chaining / nullish coalescing) —
 * see the deploy-guard JS syntax gate. First user: song-generator's
 * «Расставить ударения» flow (ё carries stress, so it runs before the
 * stress-dictionary pass). Pattern reference: russian-stress tool.
 */
(function () {
    'use strict';

    if (window.RuYofication) { return; }

    var DICT_SRC = '/vendor/eyo/bundle.safe.js';
    var loadPromise = null;

    function eyoUsable() {
        return !!(window.safeEyo && typeof window.safeEyo.restore === 'function');
    }

    function loadScript(src) {
        return new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.src = src;
            s.async = true;
            s.onload = resolve;
            s.onerror = reject;
            document.head.appendChild(s);
        });
    }

    function ready() {
        if (eyoUsable()) { return Promise.resolve(true); }
        if (loadPromise) { return loadPromise; }
        loadPromise = loadScript(DICT_SRC)
            .then(function () { return eyoUsable(); })
            .catch(function () {
                loadPromise = null; // transient network failure → retryable
                return false;
            });
        return loadPromise;
    }

    function restore(text) {
        var out = { text: text, count: 0 };
        if (typeof text !== 'string' || text === '' || !eyoUsable()) { return out; }
        try {
            var restored = window.safeEyo.restore(text);
            if (typeof restored !== 'string' || restored === text) { return out; }
            var count = 0;
            // Preferred: the kernel's own linter — one entry per replaced word
            // occurrence (ungrouped). Falls back to a char-diff count (е→ё is
            // 1:1, lengths stay equal), floored at 1 since the text DID change.
            try {
                if (typeof window.safeEyo.lint === 'function') {
                    var hits = window.safeEyo.lint(text);
                    count = (hits && hits.length) ? hits.length : 0;
                }
            } catch (eLint) { count = 0; }
            if (count === 0) {
                var n = Math.min(text.length, restored.length);
                for (var i = 0; i < n; i++) {
                    if (text.charAt(i) !== restored.charAt(i)) { count++; }
                }
                if (count === 0) { count = 1; }
            }
            out.text = restored;
            out.count = count;
        } catch (e) {
            out.text = text;
            out.count = 0;
        }
        return out;
    }

    /* Words of a transcript, in place: [{text}] → how many words changed. Word by word on
     * purpose — SAFE replacements are unambiguous without context, and each word keeps its
     * own timing (first user: /auto-captions, where Whisper writes «нашел», «ее»). */
    function restoreWords(words) {
        var changed = 0;
        if (!words || !eyoUsable()) { return 0; }
        for (var i = 0; i < words.length; i++) {
            var w = words[i];
            if (!w || typeof w.text !== 'string' || !/[Ее]/.test(w.text)) { continue; }
            var r = restore(w.text);
            if (r.count > 0) { w.text = r.text; changed++; }
        }
        return changed;
    }

    window.RuYofication = { ready: ready, restore: restore, restoreWords: restoreWords };
})();
